import { useState, useEffect } from 'react';
import { sortByName, sortByTagId } from '../lib/sortByName';
import { useTranslation } from 'react-i18next';
import { organizationsAPI, cardsAPI, hotelRoomsAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import {
    BedDouble, Search, Plus, MoreVertical, Edit2, Trash2, CreditCard, Building2, Loader2, Link2, Layers, ShieldAlert,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Button } from '../components/ui/button';
import { SetupWalkthrough } from '../components/setup_guide/SetupWalkthrough';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { Label } from '../components/ui/label';

const TP_LABELS = {
    welcome: 'Wi-Fi / Room',
    lobby_wifi: 'Wi-Fi / Lobby',
    checkout: 'Checkout',
    in_stay: 'Bathroom / In-Stay',
    room_feedback: 'Welcome / Room',
};

const HotelRooms = () => {
    const { t } = useTranslation();
    const { isSuperAdmin } = useAuth();
    const [rooms, setRooms] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [cards, setCards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterOrg, setFilterOrg] = useState('all');
    const [freeEntry, setFreeEntry] = useState(false);
    const [freeEntrySaving, setFreeEntrySaving] = useState(false);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingRoom, setEditingRoom] = useState(null);
    const [formData, setFormData] = useState({ organization_id: '', room_number: '', notes: '' });

    const [assignRoom, setAssignRoom] = useState(null);
    const [assignCardId, setAssignCardId] = useState('');
    const [saving, setSaving] = useState(false);

    // Hotel Ops (P1) — floor / room-range creation
    const [rangeOpen, setRangeOpen] = useState(false);
    const [rangeData, setRangeData] = useState({ organization_id: '', floor: '', from_room: '', to_room: '', ops_card_id: '' });

    useEffect(() => { loadData(); }, [filterOrg]);

    useEffect(() => {
        if (!isSuperAdmin() || filterOrg === 'all') return;
        hotelRoomsAPI.getFreeEntry(filterOrg)
            .then(r => setFreeEntry(!!r.data?.allow_free_room_entry))
            .catch(() => setFreeEntry(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterOrg]);

    const saveFreeEntry = async (next) => {
        setFreeEntrySaving(true);
        try {
            const r = await hotelRoomsAPI.setFreeEntry(filterOrg, next);
            setFreeEntry(!!r.data?.allow_free_room_entry);
            toast.success(t('hotelRooms.freeEntrySaved', 'Setting saved'));
        } catch {
            toast.error(t('hotelRooms.freeEntryFailed', 'Could not save that setting'));
        } finally {
            setFreeEntrySaving(false);
        }
    };

    const loadData = async () => {
        try {
            const [roomsRes, orgsRes, cardsRes] = await Promise.all([
                hotelRoomsAPI.list(filterOrg !== 'all' ? filterOrg : undefined),
                organizationsAPI.list(),
                cardsAPI.list(),
            ]);
            setRooms(roomsRes.data);
            setOrganizations(sortByName(orgsRes.data));
            setCards(sortByTagId(cardsRes.data));
        } catch (e) {
            toast.error(t('hotelRooms.loadFailed', 'Failed to load rooms'));
        } finally { setLoading(false); }
    };

    const [walkRoomOpen, setWalkRoomOpen] = useState(false);

    const openModal = (room = null) => {
        setEditingRoom(room);
        setFormData(room
            ? { organization_id: room.organization_id, room_number: room.room_number, floor: room.floor || '', notes: room.notes || '' }
            : { organization_id: filterOrg !== 'all' ? filterOrg : (organizations[0]?.id || ''), room_number: '', floor: '', notes: '' });
        setIsModalOpen(true);
    };

    const handleSave = async () => {
        if (!formData.room_number.trim() || !formData.organization_id) {
            toast.error(t('hotelRooms.roomNumberRequired', 'Organization and room number are required'));
            return;
        }
        setSaving(true);
        try {
            if (editingRoom) {
                await hotelRoomsAPI.update(editingRoom.id, formData);
                toast.success(t('hotelRooms.roomUpdated', 'Room updated'));
            } else {
                await hotelRoomsAPI.create(formData);
                toast.success(t('hotelRooms.roomCreated', 'Room created'));
            }
            setIsModalOpen(false);
            loadData();
        } catch (e) {
            toast.error(e.response?.data?.detail || t('hotelRooms.saveFailed', 'Failed to save room'));
        } finally { setSaving(false); }
    };

    const handleDelete = async (room) => {
        if (!window.confirm(t('hotelRooms.deleteConfirm', 'Delete this room? Assigned cards will be unassigned (cards themselves are untouched).'))) return;
        try {
            await hotelRoomsAPI.delete(room.id);
            toast.success(t('hotelRooms.roomDeleted', 'Room deleted'));
            loadData();
        } catch (e) {
            toast.error(e.response?.data?.detail || t('hotelRooms.deleteFailed', 'Failed to delete room'));
        }
    };

    const openRange = () => {
        setRangeData({
            organization_id: filterOrg !== 'all' ? filterOrg : (organizations[0]?.id || ''),
            floor: '', from_room: '', to_room: '', ops_card_id: '',
        });
        setRangeOpen(true);
    };

    const handleCreateRange = async () => {
        if (!rangeData.organization_id || !rangeData.floor.trim() || !rangeData.from_room.trim() || !rangeData.to_room.trim()) {
            toast.error(t('hotelRooms.rangeRequired', 'Organization, floor, from and to room numbers are required'));
            return;
        }
        setSaving(true);
        try {
            const payload = { ...rangeData };
            if (!payload.ops_card_id) delete payload.ops_card_id;
            const res = await hotelRoomsAPI.createRange(payload);
            const d = res.data;
            toast.success(t('hotelRooms.rangeCreated', `Floor ${d.floor}: ${d.created_count} rooms created, ${d.skipped_count} already existed`));
            setRangeOpen(false);
            loadData();
        } catch (e) {
            toast.error(e.response?.data?.detail || t('hotelRooms.rangeFailed', 'Failed to create room range'));
        } finally { setSaving(false); }
    };

    const handleAssign = async () => {
        if (!assignCardId) return;
        setSaving(true);
        try {
            await hotelRoomsAPI.assignCard(assignRoom.id, assignCardId, 'assign');
            toast.success(t('hotelRooms.cardAssigned', 'Card assigned to room'));
            setAssignRoom(null);
            setAssignCardId('');
            loadData();
        } catch (e) {
            toast.error(e.response?.data?.detail || t('hotelRooms.assignFailed', 'Failed to assign card'));
        } finally { setSaving(false); }
    };

    const handleUnassign = async (room, card) => {
        try {
            await hotelRoomsAPI.assignCard(room.id, card.id, 'unassign');
            toast.success(t('hotelRooms.cardUnassigned', 'Card unassigned'));
            loadData();
        } catch (e) {
            toast.error(e.response?.data?.detail || t('hotelRooms.assignFailed', 'Failed to update assignment'));
        }
    };

    const orgName = (id) => organizations.find(o => o.id === id)?.name || '-';

    // Context-aware terminology (Phase 2). Determined by the SELECTED organization
    // in the org filter only. property_management ⇒ Units; hotel/restaurant ⇒ Rooms
    // (unchanged); "All Organizations" ⇒ neutral "Rooms / Units". No per-card detection.
    const selectedOrg = filterOrg !== 'all' ? organizations.find(o => o.id === filterOrg) : null;
    const isProperty = selectedOrg?.vertical === 'property_management';
    const isAllOrgs = filterOrg === 'all';
    const V = isProperty ? {
        title: t('hotelRooms.unitTitle', 'Units'),
        subtitle: t('hotelRooms.unitSubtitle', 'Manage units and their assigned Tap Cards'),
        addEntity: t('hotelRooms.addUnit', 'Add Unit'),
        addRange: t('hotelRooms.addUnitRange', 'Add Unit Range'),
        entity: t('hotelRooms.unit', 'Unit'),
        entityNumberLabel: t('hotelRooms.unitNumberLabel', 'Unit Number'),
        addCard: t('hotelRooms.addUnitCard', 'Add Unit Card'),
        assignCard: t('hotelRooms.assignUnitCard', 'Assign Unit Card'),
        assignCardTo: t('hotelRooms.assignUnitCardTo', 'Assign Unit Card to Unit'),
        editEntity: t('hotelRooms.editUnit', 'Edit Unit'),
        noEntities: t('hotelRooms.noUnits', 'No units yet. Add your first unit and assign existing Tap Engine Cards to it.'),
        helpText: t('cardGuide.unitsHelp', 'How do I assign a Tap Card to a unit?'),
        guideHash: '#units',
        flow: 'unit',
        orgLabel: t('hotelRooms.orgLabelProperty', 'Organization / Property'),
        searchPlaceholder: t('hotelRooms.unitSearchPlaceholder', 'Search by unit number or card ID…'),
    } : isAllOrgs ? {
        title: t('hotelRooms.neutralTitle', 'Rooms / Units'),
        subtitle: t('hotelRooms.neutralSubtitle', 'Manage rooms/units and their assigned Tap Cards'),
        addEntity: t('hotelRooms.addRoomOrUnit', 'Add Room / Unit'),
        addRange: t('hotelRooms.addRoomRange', 'Add Room Range'),
        entity: t('hotelRooms.room', 'Room'),
        entityNumberLabel: t('hotelRooms.roomNumberLabel', 'Room Number'),
        addCard: t('hotelRooms.addTapCard', 'Add Tap Card'),
        assignCard: t('hotelRooms.assignCard', 'Assign Tap Card'),
        assignCardTo: t('hotelRooms.assignCardTo', 'Assign Tap Card to Room'),
        editEntity: t('hotelRooms.editRoom', 'Edit Room'),
        noEntities: t('hotelRooms.neutralNoRooms', 'No rooms or units yet. Add your first one and assign existing Tap Engine Cards to it.'),
        helpText: t('cardGuide.roomsHelp', 'How do I assign a Tap Card to a room?'),
        guideHash: '#rooms',
        flow: 'room',
        orgLabel: t('hotelRooms.organization', 'Organization / Hotel'),
        searchPlaceholder: t('hotelRooms.searchPlaceholder', 'Search by room number or card ID…'),
    } : {
        title: t('hotelRooms.title', 'Rooms'),
        subtitle: t('hotelRooms.subtitle', 'Manage rooms and their assigned Tap Cards'),
        addEntity: t('hotelRooms.addRoom', 'Add Room'),
        addRange: t('hotelRooms.addRoomRange', 'Add Room Range'),
        entity: t('hotelRooms.room', 'Room'),
        entityNumberLabel: t('hotelRooms.roomNumberLabel', 'Room Number'),
        addCard: t('hotelRooms.addTapCard', 'Add Tap Card'),
        assignCard: t('hotelRooms.assignCard', 'Assign Tap Card'),
        assignCardTo: t('hotelRooms.assignCardTo', 'Assign Tap Card to Room'),
        editEntity: t('hotelRooms.editRoom', 'Edit Room'),
        noEntities: t('hotelRooms.noRooms', 'No rooms yet. Add your first room and assign existing Tap Engine Cards to it.'),
        helpText: t('cardGuide.roomsHelp', 'How do I assign a Tap Card to a room?'),
        guideHash: '#rooms',
        flow: 'room',
        orgLabel: t('hotelRooms.organization', 'Organization / Hotel'),
        searchPlaceholder: t('hotelRooms.searchPlaceholder', 'Search by room number or card ID…'),
    };

    // Hotel Ops cards for the selected org (assigned to a FLOOR, not rooms)
    const opsCardsForRange = rangeData.organization_id
        ? cards.filter(c => c.organization_id === rangeData.organization_id && c.interaction_mode === 'hotel_staff')
        : [];
    const assignableCards = assignRoom
        ? cards.filter(c => c.organization_id === assignRoom.organization_id && !c.room_id)
        : [];
    const filteredRooms = rooms.filter(r =>
        !searchTerm ||
        r.room_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (r.cards || []).some(c => c.card_id.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    if (loading) {
        return <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-amber-500" /></div>;
    }

    return (
        <div className="space-y-6" data-testid="hotel-rooms-page">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
                        <BedDouble className="w-6 h-6 text-amber-500" />
                        {V.title}
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        {V.subtitle}
                        {' '}<a href={`/card-guide${V.guideHash}`} className="text-xs text-amber-600 underline" data-testid="rooms-card-guide-link">{V.helpText}</a>
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" onClick={openRange} data-testid="add-room-range-btn">
                        <Layers className="w-4 h-4 mr-1.5" />{V.addRange}
                    </Button>
                    <Button onClick={() => setWalkRoomOpen(true)} data-testid="add-room-btn">
                        <Plus className="w-4 h-4 mr-1.5" />{V.addEntity}
                    </Button>
                </div>
                <SetupWalkthrough
                    open={walkRoomOpen}
                    flow={V.flow}
                    onClose={() => { setWalkRoomOpen(false); openModal(); }}
                />
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                        placeholder={V.searchPlaceholder}
                        className="pl-9" data-testid="room-search-input" />
                </div>
                {(isSuperAdmin() || organizations.length > 1) && (
                    <Select value={filterOrg} onValueChange={setFilterOrg}>
                        <SelectTrigger className="sm:w-64" data-testid="room-org-filter">
                            <Building2 className="w-4 h-4 mr-1.5 text-muted-foreground" />
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">{t('hotelRooms.allOrganizations', 'All Organizations')}</SelectItem>
                            {organizations.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                )}
            </div>

            {/* Super admin only: the per-org free room entry opt-in. Off by
                default — a hotel with no room list rejects unknown rooms. */}
            {isSuperAdmin() && filterOrg !== 'all' && (
                <Card className="border-amber-500/30" data-testid="free-entry-card">
                    <CardContent className="p-4 flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3 min-w-0">
                            <ShieldAlert className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                            <div className="min-w-0">
                                <p className="text-sm font-semibold text-foreground" data-testid="free-entry-title">
                                    {t('hotelRooms.freeEntryTitle', 'Allow entry without a room list')}
                                </p>
                                <p className="text-xs text-muted-foreground mt-0.5" data-testid="free-entry-warning">
                                    {t('hotelRooms.freeEntryWarning', 'Lets anyone with the card link start a guest session without a room list. Not recommended.')}
                                </p>
                            </div>
                        </div>
                        <Switch
                            checked={freeEntry}
                            disabled={freeEntrySaving}
                            onCheckedChange={saveFreeEntry}
                            data-testid="free-entry-toggle"
                        />
                    </CardContent>
                </Card>
            )}

            {filteredRooms.length === 0 ? (
                <Card>
                    <CardContent className="py-12 text-center text-muted-foreground">
                        <BedDouble className="w-10 h-10 mx-auto mb-3 opacity-40" />
                        {V.noEntities}
                    </CardContent>
                </Card>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {filteredRooms.map(room => (
                        <Card key={room.id} data-testid={`room-card-${room.room_number}`}>
                            <CardContent className="p-4 space-y-3">
                                <div className="flex items-start justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="w-11 h-11 rounded-full bg-amber-500/15 text-amber-600 flex items-center justify-center font-bold">
                                            {room.room_number}
                                        </div>
                                        <div>
                                            <p className="font-semibold" data-testid={`room-number-${room.room_number}`}>
                                                {V.entity} {room.room_number}
                                            </p>
                                            <div className="text-xs text-muted-foreground flex items-center gap-1">
                                                <Building2 className="w-3 h-3" />{orgName(room.organization_id)}
                                                {room.floor && (
                                                    <Badge variant="outline" className="text-[10px] bg-indigo-500/10 text-indigo-500 border-indigo-500/30 ml-1" data-testid={`room-floor-${room.room_number}`}>
                                                        <Layers className="w-2.5 h-2.5 mr-0.5" />{t('hotelRooms.floor', 'Floor')} {room.floor}
                                                    </Badge>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`room-menu-${room.room_number}`}>
                                                <MoreVertical className="w-4 h-4" />
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end">
                                            <DropdownMenuItem onClick={() => { setAssignRoom(room); setAssignCardId(''); }} data-testid={`room-assign-card-${room.room_number}`}>
                                                <Link2 className="w-4 h-4 mr-2" />{V.assignCard}
                                            </DropdownMenuItem>
                                            <DropdownMenuItem onClick={() => openModal(room)}>
                                                <Edit2 className="w-4 h-4 mr-2" />{t('common.edit', 'Edit')}
                                            </DropdownMenuItem>
                                            <DropdownMenuSeparator />
                                            <DropdownMenuItem className="text-red-500" onClick={() => handleDelete(room)}>
                                                <Trash2 className="w-4 h-4 mr-2" />{t('common.delete', 'Delete')}
                                            </DropdownMenuItem>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </div>

                                <div className="space-y-1.5" data-testid={`room-cards-${room.room_number}`}>
                                    {(room.cards || []).length === 0 ? (
                                        <p className="text-xs text-muted-foreground italic">
                                            {t('hotelRooms.noCards', 'No Tap Cards assigned')}
                                        </p>
                                    ) : room.cards.map(c => (
                                        <div key={c.id} className="flex items-center justify-between border rounded-md px-2.5 py-1.5">
                                            <div className="flex items-center gap-2 min-w-0">
                                                <CreditCard className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                                                <span className="text-sm font-mono font-semibold">{c.card_id}</span>
                                                {c.touchpoint_type && (
                                                    <Badge variant="outline" className="text-[10px] bg-teal-500/10 text-teal-500 border-teal-500/30 truncate">
                                                        {TP_LABELS[c.touchpoint_type] || c.touchpoint_type}
                                                    </Badge>
                                                )}
                                                <Badge variant="outline" className={`text-[10px] ${c.status === 'active'
                                                    ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30'
                                                    : 'bg-slate-500/10 text-slate-400'}`}>
                                                    {c.status === 'active' ? t('cards.active', 'Active') : c.status}
                                                </Badge>
                                            </div>
                                            <button onClick={() => handleUnassign(room, c)}
                                                className="text-xs text-muted-foreground hover:text-red-500 ml-2 shrink-0"
                                                data-testid={`unassign-card-${c.card_id}`}>
                                                ✕
                                            </button>
                                        </div>
                                    ))}
                                </div>

                                <div className="flex items-center justify-between text-xs text-muted-foreground border-t pt-2">
                                    <span>{t('hotelRooms.totalTaps', 'Total Taps')}</span>
                                    <span className="font-bold text-foreground">{room.total_taps || 0}</span>
                                </div>

                                <Button variant="outline" size="sm" className="w-full"
                                    onClick={() => { setAssignRoom(room); setAssignCardId(''); }}
                                    data-testid={`add-tap-card-btn-${room.room_number}`}>
                                    <Plus className="w-3.5 h-3.5 mr-1.5" />{V.addCard}
                                </Button>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {/* Add / Edit room */}
            <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
                <DialogContent data-testid="room-modal">
                    <DialogHeader>
                        <DialogTitle>{editingRoom ? V.editEntity : V.addEntity}</DialogTitle>
                        <DialogDescription>{V.subtitle}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label>{V.orgLabel}</Label>
                            <Select value={formData.organization_id}
                                onValueChange={v => setFormData({ ...formData, organization_id: v })}
                                disabled={Boolean(editingRoom)}>
                                <SelectTrigger className="mt-1" data-testid="room-form-org">
                                    <SelectValue placeholder={t('hotelRooms.selectOrganization', 'Select organization')} />
                                </SelectTrigger>
                                <SelectContent>
                                    {organizations.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>{V.entityNumberLabel}</Label>
                            <Input value={formData.room_number}
                                onChange={e => setFormData({ ...formData, room_number: e.target.value })}
                                placeholder="105" className="mt-1" data-testid="room-form-number" />
                        </div>
                        <div>
                            <Label>{t('hotelRooms.floorLabel', 'Floor (optional)')}</Label>
                            <Input value={formData.floor}
                                onChange={e => setFormData({ ...formData, floor: e.target.value })}
                                placeholder="3" className="mt-1" data-testid="room-form-floor" />
                        </div>
                        <div>
                            <Label>{t('hotelRooms.notes', 'Notes (optional)')}</Label>
                            <Input value={formData.notes}
                                onChange={e => setFormData({ ...formData, notes: e.target.value })}
                                placeholder={t('hotelRooms.notesPlaceholder', 'Floor 1, near elevator…')} className="mt-1" data-testid="room-form-notes" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsModalOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
                        <Button onClick={handleSave} disabled={saving} data-testid="room-form-save">
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t('common.save', 'Save')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Assign card */}
            <Dialog open={Boolean(assignRoom)} onOpenChange={(o) => !o && setAssignRoom(null)}>
                <DialogContent data-testid="assign-card-modal">
                    <DialogHeader>
                        <DialogTitle>
                            {V.assignCardTo} {assignRoom?.room_number}
                        </DialogTitle>
                        <DialogDescription>
                            {t('hotelRooms.assignHint', 'Existing Tap Engine Cards only — the card keeps its unique ID and permanent URL. Multiple cards (WiFi, Welcome, Room Feedback…) can belong to the same room.')}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <Select value={assignCardId} onValueChange={setAssignCardId}>
                            <SelectTrigger data-testid="assign-card-select">
                                <SelectValue placeholder={t('hotelRooms.selectCard', 'Select a Tap Engine Card')} />
                            </SelectTrigger>
                            <SelectContent>
                                {assignableCards.length === 0 ? (
                                    <SelectItem value="__none__" disabled>{t('hotelRooms.noAvailableCards', 'No unassigned cards in this organization')}</SelectItem>
                                ) : assignableCards.map(c => (
                                    <SelectItem key={c.id} value={c.id}>
                                        {c.card_id}{c.name ? ` — ${c.name}` : ''} ({c.interaction_mode || 'default'})
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setAssignRoom(null)}>{t('common.cancel', 'Cancel')}</Button>
                        <Button onClick={handleAssign} disabled={!assignCardId || saving} data-testid="assign-card-confirm">
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t('hotelRooms.assign', 'Assign')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            {/* Hotel Ops (P1) — Add Room Range (a whole floor at once) */}
            <Dialog open={rangeOpen} onOpenChange={setRangeOpen}>
                <DialogContent data-testid="room-range-modal">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Layers className="w-5 h-5 text-indigo-500" />{V.addRange}
                        </DialogTitle>
                        <DialogDescription>
                            {t('hotelRooms.rangeHint', 'Create every room on a floor at once. The Ops card is assigned to the FLOOR — the room range establishes which rooms belong to it. Re-running is safe: existing rooms are skipped.')}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label>{V.orgLabel}</Label>
                            <Select value={rangeData.organization_id}
                                onValueChange={v => setRangeData({ ...rangeData, organization_id: v, ops_card_id: '' })}>
                                <SelectTrigger className="mt-1" data-testid="range-form-org">
                                    <SelectValue placeholder={t('hotelRooms.selectOrganization', 'Select organization')} />
                                </SelectTrigger>
                                <SelectContent>
                                    {organizations.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>{t('hotelRooms.floorLabel', 'Floor')}</Label>
                            <Input value={rangeData.floor}
                                onChange={e => setRangeData({ ...rangeData, floor: e.target.value })}
                                placeholder="3" className="mt-1" data-testid="range-form-floor" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>{t('hotelRooms.fromRoom', 'From Room')}</Label>
                                <Input value={rangeData.from_room}
                                    onChange={e => setRangeData({ ...rangeData, from_room: e.target.value })}
                                    placeholder="3001" className="mt-1" data-testid="range-form-from" />
                            </div>
                            <div>
                                <Label>{t('hotelRooms.toRoom', 'To Room')}</Label>
                                <Input value={rangeData.to_room}
                                    onChange={e => setRangeData({ ...rangeData, to_room: e.target.value })}
                                    placeholder="3100" className="mt-1" data-testid="range-form-to" />
                            </div>
                        </div>
                        <div>
                            <Label>{t('hotelRooms.opsCardFloor', 'Ops Card (assigned to this floor — optional)')}</Label>
                            <Select value={rangeData.ops_card_id || undefined}
                                onValueChange={v => setRangeData({ ...rangeData, ops_card_id: v })}>
                                <SelectTrigger className="mt-1" data-testid="range-form-ops-card">
                                    <SelectValue placeholder={t('hotelRooms.selectOpsCard', 'Select a Hotel Ops card')} />
                                </SelectTrigger>
                                <SelectContent>
                                    {opsCardsForRange.length === 0 ? (
                                        <SelectItem value="__none__" disabled>{t('hotelRooms.noOpsCards', 'No Hotel Ops cards in this organization')}</SelectItem>
                                    ) : opsCardsForRange.map(c => (
                                        <SelectItem key={c.id} value={c.id}>
                                            {c.card_id}{c.name ? ` — ${c.name}` : ''}{c.floor ? ` (${t('hotelRooms.floor', 'Floor')} ${c.floor})` : ''}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <p className="text-[11px] text-muted-foreground mt-1">
                                {t('hotelRooms.opsCardFloorHint', 'The card represents the whole floor, not individual rooms.')}
                            </p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRangeOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
                        <Button onClick={handleCreateRange} disabled={saving} data-testid="range-form-save">
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t('hotelRooms.createRange', 'Add Room Range')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default HotelRooms;
